// This file contains the core components for the JSA form part of the DEL. This will also be used to create a stand alone JSA form as well.

// JSA Core Components will include the Basic Info, RAC Info, Training Info, Training Employees List, Employee Signatures, Non Employee Signatures.

import React, { useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { DelDatePicker } from "../../common/DelDatePicker";
import { TimePickerInput } from "../../common/TimePickerInput";
import CurrentDateTimeButton from "../../common/CurrentDateTimeButton";
import styles from "./jsaBasicInfoComponents.module.css";
import { jsaSupervisorSelectStyles } from "../../common/delSelect";
import { useAppStore } from "../../../store/appStore";

/**
 * Helper to get field error from autosaveErrors state
 * @param {Object} autosaveErrors - The autosaveErrors state from callbacks
 * @param {string} fieldName - The field name (e.g., "jsa_client_name")
 * @returns {string|null} Error message or null
 */
const getFieldError = (autosaveErrors, fieldName) => {
  const key = `jsa_basic_info.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[key] || null;
};

// Compute touch-aware React Select props to avoid iOS virtual keyboard detaching menus
const useTouchAwareSelectProps = () => {
  const isTouch = useAppStore((s) => s.landingPage?.isTouch);
  const portalTarget =
    typeof document !== "undefined" ? document.body : undefined;
  if (isTouch) {
    return {
      menuPosition: "absolute",
      menuPlacement: "auto",
      menuShouldScrollIntoView: false,
      menuPortalTarget: portalTarget,
      styles: {
        menu: (base) => ({ ...base, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      },
    };
  }
  return {
    menuPosition: "fixed",
    menuPortalTarget: portalTarget,
    styles: {
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
  };
};

// Site Location Address Input
const SiteLocationAddressInput = ({ callbacks }) => {
  // Get autosave error for site_location field
  const autosaveErrors = callbacks.autosaveErrors || {};
  const siteLocationError = autosaveErrors?.fieldErrors?.["jsa_basic_info.site_location"] || null;

  const showNotification = useAppStore((s) => s.notificationActions.showNotification);
  const isViewMode = callbacks.jsaMode === "view";

  // GPS controls local UI state
  const [isLocating, setIsLocating] = useState(false);
  const [showCoordEntry, setShowCoordEntry] = useState(false);
  const [latInput, setLatInput] = useState("");
  const [lngInput, setLngInput] = useState("");

  // Persist site location + auto-filled medical facility via the commit bundle
  // (used by the Sampling Event form, which saves through commit, not emitPatch).
  const commitSiteAndFacility = () => {
    try {
      const bi = callbacks?.jsaData?.jsaBasicInfo;
      callbacks?.commit?.basicInfo?.(
        callbacks.jsaData,
        "site_location",
        bi?.siteLocation?.label || ""
      );
      callbacks?.commit?.basicInfo?.(
        callbacks.jsaData,
        "medical_facility",
        bi?.medicalFacility || ""
      );
      callbacks?.commit?.basicInfo?.(
        callbacks.jsaData,
        "medical_facility_address",
        bi?.medicalFacilityAddress || ""
      );
    } catch {}
  };

  // Use the device's current GPS position as the site location
  const handleUseCurrentLocation = async () => {
    setIsLocating(true);
    try {
      const res = await callbacks.coreActions.useCurrentLocationForSite(
        callbacks.jsaData,
        callbacks.emitPatch
      );
      if (!res?.ok) {
        showNotification({
          type: "error",
          title: "Location Unavailable",
          message: res?.error || "Could not get your current location.",
        });
        return;
      }
      commitSiteAndFacility();
    } finally {
      setIsLocating(false);
    }
  };

  // Apply manually-entered GPS coordinates as the site location
  const handleApplyCoordinates = async () => {
    const res = await callbacks.coreActions.applyManualCoordinates(
      callbacks.jsaData,
      latInput,
      lngInput,
      callbacks.emitPatch
    );
    if (!res?.ok) {
      showNotification({
        type: "error",
        title: "Invalid Coordinates",
        message: res?.error || "Please enter valid coordinates.",
      });
      return;
    }
    commitSiteAndFacility();
    setShowCoordEntry(false);
    setLatInput("");
    setLngInput("");
  };

  // Handling whether we are using address validation or not.
  const handleUpdateAddressValidation = (e) => {
    callbacks.coreActions.updateAddressValidation(e.target.checked);
  };

  // Handling the updated site location
  const handleUpdateSiteLocation = (selected) => {
    callbacks.coreActions.updateSiteLocation(
      callbacks.jsaData,
      selected,
      callbacks.jsaCore.googleSessionToken,
      callbacks.emitPatch
    );
    // Commit immediately if commit bundle is provided
    try {
      const label =
        selected?.label ||
        callbacks?.jsaData?.jsaBasicInfo?.siteLocation?.label ||
        null;
      callbacks?.commit?.basicInfo?.(callbacks.jsaData, "site_location", label);
    } catch {}
  };

  // Handling the manual update of the site location (local state only on onChange)
  const handleManualUpdateSiteLocation = (e) => {
    callbacks.coreActions.updateManualSiteLocation(
      callbacks.jsaData,
      e.target.value,
      null // Don't emit on every keystroke
    );
  };
  // Send patch to server on blur
  const handleBlurManualSiteLocation = (e) => {
    callbacks.coreActions.updateManualSiteLocation(
      callbacks.jsaData,
      e.target.value,
      callbacks.emitPatch // Emit on blur
    );
    try {
      callbacks?.commit?.basicInfo?.(
        callbacks.jsaData,
        "site_location",
        callbacks?.jsaData?.jsaBasicInfo?.siteLocation?.label || ""
      );
    } catch {}
  };

  // Handling loading of address suggestions
  const handleLoadAddressSuggestions = async (inputValue) => {
    const suggestions = await callbacks.coreActions.loadAddressSuggestions(
      inputValue,
      callbacks.jsaCore.googleSessionToken
    );
    return suggestions;
  };

  const touchSelectProps = useTouchAwareSelectProps();
  return (
    <>
      <div className={styles.jsaBasicInfoInput}>
        <div className={styles.jsaBasicInfoAddressValidationContainer}>
          <label>Use Address Validation: </label>
          <input
            type="checkbox"
            checked={callbacks.jsaCore.basicInfo.useAddressValidation}
            onChange={handleUpdateAddressValidation}
            disabled={callbacks.jsaMode === "view"}
          />
        </div>
      </div>
      <div className={styles.jsaBasicInfoInput}>
        <label>Site Location: </label>
        {callbacks.jsaCore.basicInfo.useAddressValidation ? (
          <AsyncSelect
            cacheOptions={false}
            defaultOptions={false}
            value={callbacks.jsaData.jsaBasicInfo.siteLocation}
            loadOptions={handleLoadAddressSuggestions}
            onChange={handleUpdateSiteLocation}
            placeholder="Type to search address..."
            noOptionsMessage={({ inputValue }) =>
              inputValue.length < 4
                ? "Please type at least 4 characters"
                : "No options found"
            }
            isDisabled={callbacks.jsaMode === "view"}
            isSearchable={true}
            styles={{
              ...jsaSupervisorSelectStyles(),
              ...(touchSelectProps.styles || {}),
            }}
            isClearable={true}
            menuPlacement="top"
            menuPosition={touchSelectProps.menuPosition}
            menuPortalTarget={touchSelectProps.menuPortalTarget}
            menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
          />
        ) : (
          <input
            type="text"
            value={
              callbacks.jsaData.jsaBasicInfo.siteLocation
                ? callbacks.jsaData.jsaBasicInfo.siteLocation.label
                : ""
            }
            onChange={handleManualUpdateSiteLocation}
            onBlur={handleBlurManualSiteLocation}
            disabled={callbacks.jsaMode === "view"}
            placeholder="Enter Address"
            clearable={true}
            className={siteLocationError ? styles.inputError : ""}
          />
        )}
        {siteLocationError && (
          <span className={styles.errorText}>{siteLocationError}</span>
        )}

        {/* GPS entry: current location + manual coordinates */}
        <div className={styles.locationActionsRow}>
          <button
            type="button"
            className={styles.locationButton}
            onClick={handleUseCurrentLocation}
            disabled={isViewMode || isLocating}
          >
            {isLocating ? "Locating…" : "Use Current Location"}
          </button>
          <button
            type="button"
            className={styles.locationButton}
            onClick={() => setShowCoordEntry((v) => !v)}
            disabled={isViewMode}
          >
            {showCoordEntry ? "Cancel Coordinates" : "Enter Coordinates"}
          </button>
        </div>
        {showCoordEntry && !isViewMode && (
          <div className={styles.coordEntryRow}>
            <input
              type="number"
              step="any"
              value={latInput}
              onChange={(e) => setLatInput(e.target.value)}
              placeholder="Latitude"
              aria-label="Latitude"
            />
            <input
              type="number"
              step="any"
              value={lngInput}
              onChange={(e) => setLngInput(e.target.value)}
              placeholder="Longitude"
              aria-label="Longitude"
            />
            <button
              type="button"
              className={styles.locationButton}
              onClick={handleApplyCoordinates}
            >
              Apply
            </button>
          </div>
        )}
      </div>
    </>
  );
};

const MedicalFacilityInput = ({ callbacks }) => {
  // Get autosave errors for medical facility fields
  const autosaveErrors = callbacks.autosaveErrors || {};
  const medicalFacilityError = autosaveErrors?.fieldErrors?.["jsa_basic_info.medical_facility"] || null;
  const medicalFacilityAddressError = autosaveErrors?.fieldErrors?.["jsa_basic_info.medical_facility_address"] || null;

  const touchSelectProps = useTouchAwareSelectProps();
  const isViewMode = callbacks.jsaMode === "view";

  // Ranked facility candidates from the lookup (transient UI state)
  const facilityOptions =
    callbacks.jsaCore?.basicInfo?.medicalFacilityOptions || [];

  // Build a dropdown label like "Baptist Health — 3.2 mi (Urgent Care)"
  const formatFacilityLabel = (o) => {
    const miles = o.distanceMiles != null ? ` — ${o.distanceMiles} mi` : "";
    const kind = o.type === "urgent_care" ? " (Urgent Care)" : "";
    return `${o.displayName}${miles}${kind}`;
  };
  const facilitySelectOptions = facilityOptions.map((o) => ({
    value: o.displayName,
    label: formatFacilityLabel(o),
    data: o,
  }));

  // Current selection: show the saved facility name even if not in the options
  // list (e.g. editing a saved JSA, where options haven't been re-fetched).
  const currentFacility = callbacks.jsaData.jsaBasicInfo.medicalFacility;
  const currentFacilityValue = currentFacility
    ? { value: currentFacility, label: currentFacility }
    : null;

  // Selecting an option sets name + address together; a created (custom) value
  // sets the name only (address typed separately); clearing resets the name.
  const handleSelectMedicalFacility = (selected) => {
    if (!selected) {
      callbacks.coreActions.updateManualMedicalFacility(
        callbacks.jsaData,
        "",
        callbacks.emitPatch
      );
      // Persist for the Sampling Event form (commit bundle).
      try {
        callbacks?.commit?.basicInfo?.(callbacks.jsaData, "medical_facility", "");
      } catch {}
      return;
    }
    if (selected.data) {
      callbacks.coreActions.selectMedicalFacility(
        callbacks.jsaData,
        selected.data,
        callbacks.emitPatch
      );
      // Option selection sets both name + address; persist both for SE.
      try {
        callbacks?.commit?.basicInfo?.(
          callbacks.jsaData,
          "medical_facility",
          callbacks?.jsaData?.jsaBasicInfo?.medicalFacility || ""
        );
        callbacks?.commit?.basicInfo?.(
          callbacks.jsaData,
          "medical_facility_address",
          callbacks?.jsaData?.jsaBasicInfo?.medicalFacilityAddress || ""
        );
      } catch {}
    } else {
      // Created/custom free-text entry (name only)
      callbacks.coreActions.updateManualMedicalFacility(
        callbacks.jsaData,
        selected.value,
        callbacks.emitPatch
      );
      try {
        callbacks?.commit?.basicInfo?.(
          callbacks.jsaData,
          "medical_facility",
          selected.value
        );
      } catch {}
    }
  };

  // Handling the manual update of the medical facility address (local state only on onChange)
  const handleManualUpdateMedicalFacilityAddress = (e) => {
    callbacks.coreActions.updateManualMedicalFacilityAddress(
      callbacks.jsaData,
      e.target.value,
      null // Don't emit on every keystroke
    );
  };
  // Send patch to server on blur
  const handleBlurMedicalFacilityAddress = (e) => {
    callbacks.coreActions.updateManualMedicalFacilityAddress(
      callbacks.jsaData,
      e.target.value,
      callbacks.emitPatch // Emit on blur
    );
    try {
      callbacks?.commit?.basicInfo?.(
        callbacks.jsaData,
        "medical_facility_address",
        callbacks?.jsaData?.jsaBasicInfo?.medicalFacilityAddress || ""
      );
    } catch {}
  };

  return (
    <>
      <div className={styles.jsaBasicInfoInput}>
        <label>Medical Facility: </label>
        <CreatableSelect
          value={currentFacilityValue}
          onChange={handleSelectMedicalFacility}
          options={facilitySelectOptions}
          isDisabled={isViewMode}
          isClearable={true}
          isSearchable={true}
          placeholder="Select or type a facility..."
          formatCreateLabel={(input) => `Use "${input}"`}
          noOptionsMessage={() => "Set a site location to see nearby facilities"}
          styles={{
            ...jsaSupervisorSelectStyles(!!medicalFacilityError),
            ...(touchSelectProps.styles || {}),
          }}
          className={medicalFacilityError ? styles.errorSelectContainer : ""}
          menuPortalTarget={touchSelectProps.menuPortalTarget}
          menuPosition={touchSelectProps.menuPosition}
          menuPlacement={touchSelectProps.menuPlacement}
          menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
        />
        {medicalFacilityError && (
          <span className={styles.errorText}>{medicalFacilityError}</span>
        )}
      </div>
      <div className={styles.jsaBasicInfoInput}>
        <label>Medical Facility Address: </label>
        <input
          type="text"
          value={callbacks.jsaData.jsaBasicInfo.medicalFacilityAddress}
          onChange={handleManualUpdateMedicalFacilityAddress}
          onBlur={handleBlurMedicalFacilityAddress}
          disabled={callbacks.jsaMode === "view"}
          className={medicalFacilityAddressError ? styles.inputError : ""}
        />
        {medicalFacilityAddressError && (
          <span className={styles.errorText}>{medicalFacilityAddressError}</span>
        )}
      </div>
    </>
  );
};

// JSA Basic Info Component
export const JsaBasicInfoTab = ({ callbacks }) => {
  // Legacy form-level errors (kept for backwards compatibility)
  const errors = callbacks.jsaData?.errors?.jsa?.basicInfo || {};
  // Autosave errors from WebSocket responses
  const autosaveErrors = callbacks.autosaveErrors || {};
  const touchSelectProps = useTouchAwareSelectProps();

  // Key to force TimePickerInput re-render when autofilled
  const [timeKey, setTimeKey] = useState(0);

  // Helper to get autosave error for a field
  const getAutosaveError = (fieldName) => getFieldError(autosaveErrors, fieldName);

  // Handling the updated client name (local state only on onChange)
  const handleUpdateClientName = (e) => {
    callbacks.coreActions.updateJsaClientName(
      callbacks.jsaData,
      e.target.value,
      null // Don't emit on every keystroke
    );
  };
  // Send patch to server on blur
  const handleBlurClientName = (e) => {
    callbacks.coreActions.updateJsaClientName(
      callbacks.jsaData,
      e.target.value,
      callbacks.emitPatch // Emit on blur
    );
    try {
      callbacks?.commit?.basicInfo?.(
        callbacks.jsaData,
        "jsa_client_name",
        callbacks.jsaData.jsaBasicInfo.jsaClientName
      );
    } catch {}
  };

  // Handling the updated job description (local state only on onChange)
  const handleUpdateJobDescription = (e) => {
    callbacks.coreActions.updateJsaJobDescription(
      callbacks.jsaData,
      e.target.value,
      null // Don't emit on every keystroke
    );
  };
  // Send patch to server on blur
  const handleBlurJobDescription = (e) => {
    callbacks.coreActions.updateJsaJobDescription(
      callbacks.jsaData,
      e.target.value,
      callbacks.emitPatch // Emit on blur
    );
    try {
      callbacks?.commit?.basicInfo?.(
        callbacks.jsaData,
        "jsa_job_description",
        callbacks.jsaData.jsaBasicInfo.jsaJobDescription
      );
    } catch {}
  };

  // Handling the updated supervisor
  const handleUpdateSupervisor = (selected) => {
    callbacks.coreActions.selectJsaSupervisor(callbacks.jsaData, selected, callbacks.emitPatch);
    try {
      const payload = selected
        ? {
            supervisor_id: selected?.value ?? null,
            supervisor_name: selected?.label ?? null,
          }
        : { supervisor_id: null, supervisor_name: null };
      callbacks?.commit?.basicInfo?.(
        callbacks.jsaData,
        "supervisor_id",
        payload
      );
    } catch {}
  };

  // Handling the updated completed time
  const handleUpdateCompletedTime = (time) => {
    callbacks.coreActions.updateJsaCompletedTime(callbacks.jsaData, time, callbacks.emitPatch);
    try {
      const value = time || null;
      callbacks?.commit?.basicInfo?.(
        callbacks.jsaData,
        "jsa_completed_time",
        value
      );
    } catch {}
  };

  // Handling the updated completed date
  const handleUpdateCompletedDate = (date) => {
    callbacks.coreActions.updateJsaCompletedDate(callbacks.jsaData, date, callbacks.emitPatch);
    callbacks?.commit?.basicInfo?.(
      callbacks.jsaData,
      "jsa_completed_date",
      callbacks.jsaData.jsaBasicInfo.jsaCompletedDate
    );
  };

  return (
    <div className={styles.jsaBasicInfoContainer}>
      {/* First row, including client name and supervisor */}
      <div className={styles.jsaBasicInfoRow}>
        <div className={styles.jsaBasicInfoInput}>
          <label>Client Name: </label>
          <input
            type="text"
            value={callbacks.jsaData.jsaBasicInfo.jsaClientName}
            onChange={handleUpdateClientName}
            onBlur={handleBlurClientName}
            disabled={callbacks.jsaMode === "view"}
            className={getAutosaveError("jsa_client_name") ? styles.inputError : ""}
          />
          {getAutosaveError("jsa_client_name") && (
            <span className={styles.errorText}>{getAutosaveError("jsa_client_name")}</span>
          )}
        </div>
        {/* Job Description */}
        <div className={styles.jsaBasicInfoInput}>
          <label>Job Description: </label>
          <input
            type="text"
            value={callbacks.jsaData.jsaBasicInfo.jsaJobDescription}
            onChange={handleUpdateJobDescription}
            onBlur={handleBlurJobDescription}
            disabled={callbacks.jsaMode === "view"}
            className={getAutosaveError("jsa_job_description") ? styles.inputError : ""}
          />
          {getAutosaveError("jsa_job_description") && (
            <span className={styles.errorText}>{getAutosaveError("jsa_job_description")}</span>
          )}
        </div>
      </div>
      <div className={styles.jsaBasicInfoRow}>
        {/* Supervisor */}
        <div className={styles.jsaBasicInfoInput}>
          <label>EWI Supervisor: </label>
          <Select
            value={
              callbacks.jsaData.jsaBasicInfo.supervisor
                ? {
                    value: callbacks.jsaData.jsaBasicInfo.supervisor.value,
                    label: callbacks.jsaData.jsaBasicInfo.supervisor.label,
                  }
                : "Select Supervisor"
            }
            onChange={handleUpdateSupervisor}
            options={callbacks.allEmployees.map((emp) => ({
              value: emp.employee_id,
              label: emp.label,
            }))}
            isDisabled={callbacks.jsaMode === "view"}
            styles={{
              ...jsaSupervisorSelectStyles(!!errors?.supervisor || !!getAutosaveError("supervisor_id")),
              ...(touchSelectProps.styles || {}),
            }}
            placeholder={
              errors?.supervisor || getAutosaveError("supervisor_id") ? (
                <span className={styles.errorText}>{errors?.supervisor || getAutosaveError("supervisor_id")}</span>
              ) : (
                "Select Employee"
              )
            }
            isClearable={true}
            isSearchable={true}
            menuPortalTarget={touchSelectProps.menuPortalTarget}
            menuPosition={touchSelectProps.menuPosition}
            menuPlacement={touchSelectProps.menuPlacement}
            menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
            className={(errors?.supervisor || getAutosaveError("supervisor_id")) ? styles.errorSelectContainer : ""}
          />
          {getAutosaveError("supervisor_id") && (
            <span className={styles.errorText}>{getAutosaveError("supervisor_id")}</span>
          )}
        </div>
      </div>
      {/* Date/Time JSA Completed Row */}
      <div className={styles.dateTimeRow}>
        <div className={styles.jsaBasicInfoInput}>
          <label>Time JSA Completed: </label>
          <TimePickerInput
            key={timeKey}
            value={callbacks.jsaData.jsaBasicInfo.jsaCompletedTime}
            onChange={handleUpdateCompletedTime}
            disabled={callbacks.jsaMode === "view"}
            className={`${(errors?.jsaCompletedTime || getAutosaveError("jsa_completed_time")) ? styles.inputError : ""}`}
          />
          {(errors?.jsaCompletedTime || getAutosaveError("jsa_completed_time")) && (
            <span className={styles.errorText}>{errors?.jsaCompletedTime || getAutosaveError("jsa_completed_time")}</span>
          )}
        </div>
        <div className={styles.jsaBasicInfoInputDate}>
          <label>Date JSA Completed: </label>
          <DelDatePicker
            selected={callbacks.jsaData.jsaBasicInfo.jsaCompletedDate}
            onChange={handleUpdateCompletedDate}
            isDisabled={callbacks.jsaMode === "view"}
            placeholderText={
              (errors?.jsaCompletedDate || getAutosaveError("jsa_completed_date"))
                ? (errors?.jsaCompletedDate || getAutosaveError("jsa_completed_date"))
                : "Select a date"
            }
            className={`${(errors?.jsaCompletedDate || getAutosaveError("jsa_completed_date")) ? styles.inputError : ""}`}
          />
          {getAutosaveError("jsa_completed_date") && (
            <span className={styles.errorText}>{getAutosaveError("jsa_completed_date")}</span>
          )}
        </div>
        <CurrentDateTimeButton
          onDateChange={handleUpdateCompletedDate}
          onTimeChange={(time) => {
            handleUpdateCompletedTime(time);
            setTimeKey((k) => k + 1);
          }}
          disabled={callbacks.jsaMode === "view"}
        />
      </div>
      <div className={styles.jsaBasicInfoRowWithColumn}>
        {/* Site Location */}
        <SiteLocationAddressInput callbacks={callbacks} />
        {/* Medical Facility */}
        <MedicalFacilityInput callbacks={callbacks} />
      </div>
    </div>
  );
};
